import { useEffect } from 'react';
import { useIsMobile } from './useIsMobile';

/**
 * On mobile, make the browser/device Back button close the topmost open modal
 * instead of navigating the page history.
 *
 * How it works (mount this ONCE at the app shell):
 *  - We watch the DOM for open overlays. Radix (Dialog / AlertDialog / Sheet)
 *    and vaul (Drawer, which is built on Radix) all render their content with
 *    role="dialog" | "alertdialog" and data-state="open", and they all close
 *    on the Escape key. So a single observer + a synthetic Escape covers every
 *    fullscreen mobile popup without touching individual modal components.
 *  - While at least one modal is open we keep exactly ONE extra history entry
 *    ("the guard") on the stack. Pressing Back pops the guard -> popstate ->
 *    we swallow the navigation and dispatch Escape to close the topmost modal.
 *    If modals remain open, we re-arm a fresh guard.
 *  - When the last modal closes through the normal UI (X / Save / outside tap)
 *    we remove the lingering guard ourselves so the next Back press behaves
 *    normally.
 *
 * Desktop is untouched (modals there are not fullscreen and Back is expected to
 * navigate); the whole effect is gated behind useIsMobile().
 */
const MODAL_SELECTOR = '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]';

export function useModalBackClose() {
  const isMobile = useIsMobile();

  useEffect(() => {
    if (!isMobile) return;

    let armed = false;   // is our guard history entry currently on the stack?
    let suppress = 0;    // popstate events to ignore (caused by our own history.back)
    let rafId = null;

    const countOpenModals = () => document.querySelectorAll(MODAL_SELECTOR).length;

    // The guard is only safe to remove with history.back() while it is the
    // top entry. If the app navigated while a modal was open (e.g. tapping a
    // link in the mobile More sheet), the router pushed a new entry ON TOP of
    // the guard, so back() would undo the user's navigation instead of
    // removing the guard (BG_94).
    const guardOnTop = () => !!(window.history.state && window.history.state.__modalGuard);

    const reconcile = () => {
      rafId = null;
      const open = countOpenModals();
      if (open > 0 && !armed) {
        // A modal just opened and we have no guard yet — push one.
        armed = true;
        window.history.pushState({ __modalGuard: true }, '', window.location.href);
      } else if (open === 0 && armed) {
        // Every modal closed via the UI — remove our lingering guard entry,
        // but only when it is still on top of the stack. Otherwise a real
        // navigation entry sits above it and we must leave history alone.
        armed = false;
        if (guardOnTop()) {
          suppress += 1;
          window.history.back();
        }
      }
    };

    const scheduleReconcile = () => {
      if (rafId != null) return;
      rafId = window.requestAnimationFrame(reconcile);
    };

    const onPopState = () => {
      if (suppress > 0) { suppress -= 1; return; }
      if (armed) {
        // Back was pressed while a modal is open: the browser already popped our
        // guard, so close the topmost modal (Radix/vaul listen for Escape).
        armed = false;
        document.dispatchEvent(new KeyboardEvent('keydown', {
          key: 'Escape', code: 'Escape', bubbles: true, cancelable: true,
        }));
        // If other modals are still open, reconcile() will re-arm a guard.
        scheduleReconcile();
      }
    };

    const observer = new MutationObserver(scheduleReconcile);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-state', 'role'],
    });
    window.addEventListener('popstate', onPopState);
    // Handle a modal that is already open on mount (e.g. a deep-linked ?task=).
    scheduleReconcile();

    return () => {
      observer.disconnect();
      window.removeEventListener('popstate', onPopState);
      if (rafId != null) window.cancelAnimationFrame(rafId);
      // Drop a lingering guard if the breakpoint flips to desktop mid-modal
      // (only when it is still on top — never undo a real navigation entry).
      if (armed && window.history.state && window.history.state.__modalGuard) window.history.back();
    };
  }, [isMobile]);
}

export default useModalBackClose;
